// Duenner Client fuer die native Ollama-HTTP-API.
// Doku: https://github.com/ollama/ollama/blob/main/docs/api.md

import { OLLAMA_BASE_URL, OLLAMA_TIMEOUT_MS } from '../config';

const url = (path) => `${OLLAMA_BASE_URL}${path}`;

/**
 * Statusabfrage mit hartem Zeitlimit.
 *
 * Ohne VPN ist 129.217.230.209 nicht routbar; je nach Netz laeuft der Verbindungs-
 * versuch sonst deutlich zu lange, bevor der Hinweis erscheint. Das eigene
 * Zeitlimit wird mit dem Signal des Aufrufers kombiniert.
 */
async function getJson(path, signal) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), OLLAMA_TIMEOUT_MS);
    const forwardAbort = () => controller.abort();
    signal?.addEventListener('abort', forwardAbort);

    try {
        const response = await fetch(url(path), {
            signal: controller.signal,
            headers: { Accept: 'application/json' },
        });
        if (!response.ok) {
            // 401/403 kommt von der Zugriffsregel im nginx: Client ist nicht im
            // Hochschulnetz. Der Status wird mitgereicht, damit die Oberflaeche
            // den VPN-Hinweis statt einer Stoerungsmeldung zeigen kann.
            const error = new Error(`${path} -> HTTP ${response.status}`);
            error.status = response.status;
            throw error;
        }
        return await response.json();
    } catch (error) {
        // Abbruch durch das eigene Zeitlimit ist ein Verbindungsfehler, kein
        // Abbruch durch den Aufrufer - sonst wuerde der Status nie aktualisiert.
        if (error.name === 'AbortError' && !signal?.aborted) {
            throw new Error('Zeitüberschreitung - keine Verbindung');
        }
        throw error;
    } finally {
        clearTimeout(timer);
        signal?.removeEventListener('abort', forwardAbort);
    }
}

/** Ollama-Version, dient gleichzeitig als Erreichbarkeits-Check. */
export async function fetchVersion(signal) {
    const data = await getJson('/api/version', signal);
    return data.version || 'unbekannt';
}

/** Alle auf dem Server installierten Modelle. */
export async function fetchModels(signal) {
    const data = await getJson('/api/tags', signal);
    return (data.models || []).map((entry) => ({
        name: entry.model || entry.name,
        size: entry.size || 0,
        parameterSize: entry.details?.parameter_size || '',
        quantization: entry.details?.quantization_level || '',
        family: entry.details?.family || '',
        modifiedAt: entry.modified_at || null,
    }));
}

/**
 * Faehigkeiten eines Modells (z. B. ["completion","tools","thinking"] oder
 * ["embedding"]). Dient dazu, reine Embedding-Modelle wie bge-m3 aus der
 * Modellauswahl herauszuhalten - die koennen /api/chat gar nicht bedienen.
 *
 * Liefert null, wenn der Server das Feld nicht kennt (aeltere Ollama-Version).
 * Der Aufrufer behaelt das Modell dann lieber, statt es faelschlich zu verstecken.
 */
export async function fetchCapabilities(model, signal) {
    try {
        const response = await fetch(url('/api/show'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            signal,
            body: JSON.stringify({ model }),
        });
        if (!response.ok) return null;
        const data = await response.json();
        return Array.isArray(data.capabilities) ? data.capabilities : null;
    } catch (error) {
        if (error.name === 'AbortError') throw error;
        return null;
    }
}

/**
 * Aktuell in den Speicher geladene Modelle.
 * Das ist geteilter Serverzustand - hierueber sieht man, ob gerade jemand anderes
 * das Modell belegt.
 */
export async function fetchLoadedModels(signal) {
    const data = await getJson('/api/ps', signal);
    return (data.models || []).map((entry) => ({
        name: entry.model || entry.name,
        size: entry.size || 0,
        sizeVram: entry.size_vram || 0,
        // expires_at wird bei jeder Anfrage neu gesetzt (keep_alive). Ein steigender
        // Wert zwischen zwei Polls bedeutet deshalb: gerade laeuft eine Anfrage.
        expiresAt: entry.expires_at ? new Date(entry.expires_at).getTime() : 0,
    }));
}

/**
 * Streamt eine Chat-Antwort.
 *
 * @param {object}   params
 * @param {string}   params.model
 * @param {Array}    params.messages   [{ role, content }]
 * @param {AbortSignal} params.signal
 * @param {(chunk: string) => void} params.onToken     wird pro Token-Fragment gerufen
 * @param {(chunk: string) => void} params.onThinking  Denk-Tokens (z. B. qwen3)
 * @returns {Promise<object>} Kennzahlen der finalen Antwort
 */
export async function streamChat({ model, messages, signal, onToken, onThinking }) {
    const response = await fetch(url('/api/chat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal,
        body: JSON.stringify({
            model,
            messages: messages.map(({ role, content }) => ({ role, content })),
            stream: true,
        }),
    });

    if (!response.ok) {
        const detail = await response.text().catch(() => '');
        throw new Error(detail || `Ollama antwortete mit HTTP ${response.status}`);
    }
    if (!response.body) {
        throw new Error('Streaming wird von diesem Browser nicht unterstuetzt.');
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let stats = null;

    const handleLine = (line) => {
        const trimmed = line.trim();
        if (!trimmed) return;

        let payload;
        try {
            payload = JSON.parse(trimmed);
        } catch {
            return; // unvollstaendige Zeile ueberspringen
        }

        if (payload.error) {
            throw new Error(payload.error);
        }
        // Modelle mit "thinking"-Faehigkeit liefern den Gedankengang in einem
        // eigenen Feld. Ohne getrennte Behandlung wirkt der Chat waehrend der
        // Denkphase wie eingefroren, weil content noch leer ist.
        const thought = payload.message?.thinking;
        if (thought && onThinking) {
            onThinking(thought);
        }
        const token = payload.message?.content;
        if (token) {
            onToken(token);
        }
        if (payload.done) {
            stats = {
                model: payload.model || model,
                totalDurationMs: (payload.total_duration || 0) / 1e6,
                loadDurationMs: (payload.load_duration || 0) / 1e6,
                promptTokens: payload.prompt_eval_count || 0,
                completionTokens: payload.eval_count || 0,
                evalDurationMs: (payload.eval_duration || 0) / 1e6,
                doneReason: payload.done_reason || 'stop',
            };
        }
    };

    // eslint-disable-next-line no-constant-condition
    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? ''; // Rest ist evtl. eine angebrochene Zeile
        lines.forEach(handleLine);
    }
    if (buffer) handleLine(buffer);

    return stats;
}
